import CustomChip from 'Components/CustomChip'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledLink from 'Components/StyledLink'
import StyledTableRow from 'Components/StyledTableRow'
import {
  type AuthorityShape,
  CampaignType,
  createMultiCodeCampaignDiscountCodes,
  fetchMultiCodeCampaignDiscountCodes,
  type MultiCodeCampaignShape
} from 'Services/SalesManagement/Features/Campaign/campaignSlice'
import { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { CircularProgress, TableCell } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, useState } from 'react'
import { t } from 'ttag'

const getAuthoritiesAsString = (authorities: AuthorityShape[]) => {
  if (authorities.length > 0) {
    return authorities.map(authority => authority[1]).join(', ')
  }
  return '-'
}

const MultiCodeCampaignListRow: FC<{
  row: MultiCodeCampaignShape
}> = ({ row }) => {
  const dispatch = useAppDispatch()
  const [isDiscountCodeActionLoading, setIsDiscountCodeActionLoading] = useState(false)
  const [campaignRow, setCampaignRow] = useState<MultiCodeCampaignShape>(row)

  const handleGenerateDiscountCodes = (campaign: MultiCodeCampaignShape) => {
    setIsDiscountCodeActionLoading(true)
    dispatch(
      createMultiCodeCampaignDiscountCodes({
        id: campaign.id
      })
    )
      .then(unwrapResult)
      .then(response => {
        setCampaignRow(response)
        setIsDiscountCodeActionLoading(false)
        SnackbarUtils.success(t`Discount codes successfully generated. Now you can download them.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error generating the discount codes.`)
      })
  }

  const handleDownloadDiscountCodes = (campaign: MultiCodeCampaignShape) => {
    setIsDiscountCodeActionLoading(true)
    dispatch(
      fetchMultiCodeCampaignDiscountCodes({
        id: campaign.id
      })
    )
      .then(unwrapResult)
      .then(response => {
        const blob = new Blob([response])
        const tempLink = document.createElement('a')
        tempLink.href = window.URL.createObjectURL(blob)
        const discountCodesName = `discount-codes-${campaign.name.toLowerCase()}`

        tempLink.setAttribute('download', `${discountCodesName}.csv`)
        document.body.appendChild(tempLink)
        tempLink.click()
        document.body.removeChild(tempLink)
        setIsDiscountCodeActionLoading(false)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error downloading the discount codes.`)
      })
  }

  const getDiscountCodeAction = (campaign: MultiCodeCampaignShape) => {
    let discountActionLabel = t`Generate codes`
    let discountAction = handleGenerateDiscountCodes
    if (campaign.has_discount_codes_generated) {
      discountActionLabel = t`Download codes`
      discountAction = handleDownloadDiscountCodes
    }
    return (
      <CustomChip
        size="small"
        color="secondary"
        label={
          isDiscountCodeActionLoading ? (
            <CircularProgress sx={{ display: 'block' }} size={16} />
          ) : (
            discountActionLabel
          )
        }
        onClick={() => discountAction(campaign)}
      />
    )
  }

  return (
    <StyledTableRow hover>
      <NoWrapTableCell title={datetimeFormatter(row.created)} component="th" scope="row">
        <StyledLink
          to={`${SALES_MANAGEMENT_ROOT_URL}/campaigns/${CampaignType.multiCode}/${campaignRow.id}/`}
        >
          {datetimeFormatter(campaignRow.created)}
        </StyledLink>
      </NoWrapTableCell>
      <NoWrapTableCell title={showValueOrHyphen(campaignRow.name)}>
        {showValueOrHyphen(campaignRow.name)}
      </NoWrapTableCell>
      <NoWrapTableCell title={datetimeFormatter(campaignRow.start_date)}>
        {datetimeFormatter(campaignRow.start_date)}
      </NoWrapTableCell>
      <NoWrapTableCell title={datetimeFormatter(campaignRow.end_date)}>
        {datetimeFormatter(campaignRow.end_date)}
      </NoWrapTableCell>
      <NoWrapTableCell title={getAuthoritiesAsString(campaignRow.authorities)}>
        {getAuthoritiesAsString(campaignRow.authorities)}
      </NoWrapTableCell>
      <TableCell>{getDiscountCodeAction(campaignRow)}</TableCell>
    </StyledTableRow>
  )
}

export { MultiCodeCampaignListRow }
